import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, BarChart3, Calendar, Briefcase, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function PartnersLanding() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [reg, setReg] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    operating_areas_text: "",
    num_boats: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reg.company_name || !reg.email) {
      toast.error(lang === "it" ? "Nome società ed email richiesti" : "Company name & email required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_name: reg.company_name,
        contact_name: reg.contact_name,
        email: reg.email,
        phone: reg.phone,
        website: reg.website,
        operating_areas: reg.operating_areas_text.split(",").map((s) => s.trim()).filter(Boolean),
        num_boats: parseInt(reg.num_boats || "0", 10),
      };
      const res = await api.post("/partners/register", payload);
      localStorage.setItem("sm_partner", JSON.stringify(res.data));
      toast.success(lang === "it" ? "Account creato!" : "Account created!");
      navigate("/partners/dashboard");
    } catch (e) {
      toast.error("Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partners-page" className="bg-white pt-20">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1700583208328-9d42bfa58da5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB5YWNodCUyMGFlcmlhbCUyMG9jZWFufGVufDB8fHx8MTc4MDMwMjQwNnww&ixlib=rb-4.1.0&q=85"
          alt="yacht aerial"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 pb-16 pt-28 w-full">
          <p className="label-eyebrow text-gold">{t("partner.heroEyebrow")}</p>
          <h1 className="mt-4 font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-tight">
            {t("partner.heroTitle")}
          </h1>
          <p className="mt-5 max-w-xl text-white/85 leading-relaxed">{t("partner.heroSub")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="partner-register-cta"
              onClick={() => {
                document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-gold text-navy hover:bg-[#c89e29] rounded-full h-12 px-7 font-semibold"
            >
              {t("partner.cta")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button
              data-testid="partner-login-cta"
              variant="outline"
              onClick={() => navigate("/partners/login")}
              className="bg-transparent border-white/60 text-white hover:bg-white/10 rounded-full h-12 px-7"
            >
              {t("partner.login")}
            </Button>
          </div>
        </div>
      </section>

      {/* BULLETS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-3 gap-6">
          {[
            { icon: BarChart3, key: "bullet1" },
            { icon: Briefcase, key: "bullet2" },
            { icon: Calendar, key: "bullet3" },
          ].map((b, i) => (
            <div key={i} className="rounded-2xl border border-[#E2E8F0] p-7">
              <b.icon className="h-5 w-5 text-turquoise" />
              <p className="mt-5 font-serif text-2xl text-navy leading-snug">{t(`partner.${b.key}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REGISTER FORM */}
      <section id="partner-form" className="py-20 bg-sand">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{t("partner.heroEyebrow")}</p>
          <h2 className="mt-3 font-serif text-4xl text-navy tracking-tight">{t("partner.registerTitle")}</h2>
          <p className="mt-3 text-ink">{t("partner.registerSub")}</p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <Field label={t("partner.companyName")} testId="reg-company" value={reg.company_name} onChange={(v) => setReg({ ...reg, company_name: v })} />
            <Field label={t("partner.contactName")} testId="reg-contact" value={reg.contact_name} onChange={(v) => setReg({ ...reg, contact_name: v })} />
            <Field label={t("partner.email")} testId="reg-email" type="email" value={reg.email} onChange={(v) => setReg({ ...reg, email: v })} />
            <Field label={t("partner.phone")} testId="reg-phone" value={reg.phone} onChange={(v) => setReg({ ...reg, phone: v })} />
            <Field label={t("partner.website")} testId="reg-website" value={reg.website} onChange={(v) => setReg({ ...reg, website: v })} />
            <Field label={t("partner.areas")} testId="reg-areas" placeholder={t("partner.areasPh")} value={reg.operating_areas_text} onChange={(v) => setReg({ ...reg, operating_areas_text: v })} />
            <div className="sm:col-span-2">
              <Field label={t("partner.numBoats")} testId="reg-boats" type="number" value={reg.num_boats} onChange={(v) => setReg({ ...reg, num_boats: v })} />
            </div>
          </div>

          <Button
            data-testid="reg-submit"
            onClick={submit}
            disabled={submitting}
            className="mt-8 bg-navy text-white hover:bg-[#06101f] h-12 px-8 rounded-md"
          >
            {submitting ? "…" : t("partner.submit")}
          </Button>

          <p className="mt-6 text-sm text-ink">
            {t("partner.already")}{" "}
            <button
              data-testid="goto-login"
              onClick={() => navigate("/partners/login")}
              className="text-navy underline underline-offset-4 hover:no-underline"
            >
              {t("partner.login")}
            </button>
          </p>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink">{label}</Label>
      <Input
        data-testid={testId}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-12 bg-white"
      />
    </div>
  );
}
